/**
 * Gift Wrap Component
 * Handles per-item gift wrapping toggles in the cart
 * Stores wrap_type property on line items for order fulfillment
 * Automatically adds/updates gift wrap product in cart to match quantity
 */

(function() {
  'use strict';

  const config = window.theme?.giftWrap;
  if (!config?.enabled) return;

  let debounceTimer = null;

  function handleCheckboxChange(e) {
    if (!e.target.matches('[data-gift-wrap]')) return;

    const checkbox = e.target;
    const lineItemKey = checkbox.dataset.lineItemKey;

    if (!lineItemKey) {
      console.error('Gift wrap: Missing line item key');
      return;
    }

    // Debounce to prevent rapid requests (500ms like legacy)
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      updateLineItemProperty(lineItemKey, checkbox.checked);
    }, 500);
  }

  async function updateLineItemProperty(id, isChecked) {
    const propertyValue = isChecked ? config.propertyValue : null;

    const body = {
      id: id,
      properties: {
        [config.propertyKey]: propertyValue
      }
    };

    try {
      const response = await fetch(window.theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();

      // Update gift wrap product quantity in cart
      await updateGiftWrapProducts(cart);

      // Dispatch event for cart drawer/page to refresh (matches legacy theme)
      document.dispatchEvent(new CustomEvent('theme:cart:updated', {
        bubbles: true,
        detail: { cart }
      }));

    } catch (error) {
      console.error('Gift wrap update failed:', error);
    }
  }

  /**
   * Automatically adds/updates the gift wrap product in the cart
   * to match the total quantity of items with gift wrap enabled.
   * Mirrors the legacy theme's updateGiftWrapProducts function.
   */
  async function updateGiftWrapProducts(cart) {
    // Skip if no gift wrap product is configured
    if (!config.productVariantId) return;

    // Find all items with gift wrap property enabled
    const lineItemsWithGiftWrap = cart.items.filter(
      (item) => item.properties && item.properties[config.propertyKey] === config.propertyValue
    );

    // Calculate total quantity of items needing gift wrap
    const targetGiftWrapQty = lineItemsWithGiftWrap.reduce(
      (total, item) => total + item.quantity, 0
    );

    // Find existing gift wrap product in cart
    const giftWrapLineItem = cart.items.find(
      (item) => item.variant_id === config.productVariantId
    );

    // Skip if quantities already match
    if (targetGiftWrapQty === (giftWrapLineItem?.quantity || 0)) {
      return;
    }

    try {
      if (targetGiftWrapQty === 0 && giftWrapLineItem) {
        // Remove gift wrap product if no items need it
        await fetch(window.theme.routes.cart_change_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: giftWrapLineItem.key,
            quantity: 0
          })
        });
      } else if (giftWrapLineItem) {
        // Update existing gift wrap product quantity
        await fetch(window.theme.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            updates: {
              [giftWrapLineItem.key]: targetGiftWrapQty
            }
          })
        });
      } else if (targetGiftWrapQty > 0) {
        // Add new gift wrap product
        await fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: config.productVariantId,
            quantity: targetGiftWrapQty
          })
        });
      }
    } catch (error) {
      console.error('Gift wrap product update failed:', error);
    }
  }

  // Use event delegation on document for dynamically loaded cart content
  document.addEventListener('change', handleCheckboxChange);
})();
